<template>
  <BaseSlideOverModal
    v-model:is-modal-open="localOpen"
    width="xl"
    :title="modalTitle"
  >
    <template #cta>
      <div class="flex h-full gap-x-1">
        <BaseButton
          v-if="taskId"
          :icon="'pen'"
          :title="$t('global.activity_log')"
          @click="activityLogModalOpened = true"
        />
        <BaseButton
          v-if="taskId"
          color="danger"
          icon="trash"
          :title="$t('button.delete')"
          :disabled="isSubmitting"
          @click="deleteTask"
        />
        <BaseButton
          v-if="editionButtonCondition"
          theme-color="bg-secondary text-contrast-secondary dark:border dark:border-secondary dark:bg-slate-700 dark:text-secondary"
          :icon="localEditionMode ? 'times' : 'pen'"
          :title="localEditionMode ? $t('button.cancel') : $t('button.edit')"
          :disabled="isSubmitting"
          @click="localEditionMode = !localEditionMode"
        />
        <BaseButton
          v-if="submitButtonCondition"
          :title="submitButtonTitle"
          theme-color="bg-primary text-contrast-primary dark:border dark:border-primary dark:bg-slate-700 dark:text-primary"
          icon="paper-plane"
          color="primary"
          :disabled="isSubmitting"
          @click="submitForm"
        />
      </div>
    </template>
    <template #body>
      <div
        v-if="!task"
        class="flex h-full w-full flex-col items-center justify-center"
      >
        <LoadingComponent />
      </div>
      <div
        v-else
        class="-mx-2 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-2"
      >
        <div>
          <SelectFuneralHome
            v-model="task.funeral_home_id"
            :errors="errors"
            :edition-mode="selectFuneralHomeCondition"
            :required="true"
            error-key="funeral_home_id"
          />
        </div>
        <div v-if="lockedDossierId">
          <SelectDossier
            :dossier-id="lockedDossierId"
            :dossier="{ id: lockedDossierId, title: defaultDossierName }"
            disabled
          />
        </div>
        <div v-else-if="selectFuneralHomeCondition">
          <SelectDossier v-model:dossier="task.parent_id" />
        </div>
        <div v-if="!task?.id">
          <BaseShowEditRadio
            v-model="type"
            name="type"
            :label="$t('attributes.type')"
            :options="[
              { id: 'new', name: $t('button.new') },
              { id: 'existing', name: $t('button.existing') }
            ]"
            :edition-mode="localEditionMode"
            required
          />
        </div>

        <template v-if="task?.id?.length || type === 'new'">
          <div>
            <BaseShowEditInput
              v-model="task.name[$i18n.locale]"
              :label="$t('attributes.name')"
              name="name"
              :errors="errors.name"
              :edition-mode="localEditionMode"
            />
          </div>
          <!--#endregion -->
        </template>
        <template v-else>
          <SelectTasks
            v-model="task.task_id"
            :edition-mode="localEditionMode"
          />
        </template>

        <!--#region remarks -->
        <div>
          <BaseShowEditTextArea
            v-model="task.remarks[$i18n.locale]"
            :label="$t('attributes.remarks')"
            name="remarks"
            :errors="errors.remarks"
            :edition-mode="localEditionMode"
          />
        </div>

        <div>
          <SelectMiniStatusTask
            v-model="task.status"
            :disabled="!localEditionMode"
            :mini="false"
          />
        </div>
        <!--#endregion -->

        <div>
          <BaseShowEditRadio
            v-model="task.due_date_type"
            name="due_date_type"
            :label="$t('attributes.due_date_type')"
            :options="initialMetadata?.enums?.due_date_types"
            :edition-mode="localEditionMode"
            :errors="errors?.due_date_type"
            :required="true"
            :translatable="false"
            :is-vertical="false"
          />
        </div>

        <div>
          <BaseShowEditDateTimePickerv2
            v-model:date="task.due_date_time"
            v-model:time-undefined="task.due_date_undefined_time"
            name="due_date_time"
            :label-date="$t('attributes.due_date')"
            :label-time="$t('attributes.hour')"
            :edition-mode="localEditionMode"
            :errors="errors ? errors.due_date_time : null"
          />
        </div>

        <!--#region manager -->
        <div>
          <SelectUser
            v-model="task.users.manager_id"
            :label="$t('attributes.responsable')"
            name="manager"
            :edition-mode="localEditionMode"
            required
          />
        </div>
        <!--#endregion -->

        <!--#region executors -->
        <div v-if="task?.users?.executors" class="my-2">
          <UsersSelectorComponent
            v-model:users-id="task.users.executors_id"
            :title="$t('attributes.executors')"
            :edition-mode="localEditionMode"
          />
        </div>
        <!--#endregion -->

        <!--#region chat -->
        <div
          v-if="!localEditionMode"
          class="mt-6 flex flex-1 flex-col rounded-md p-1.5 dark:border dark:border-white dark:bg-slate-600"
        >
          <h3 class="capitalize-first flex-shrink-0 text-xl font-bold">
            {{ $t('global.comments') }}
          </h3>
          <CommentBox
            :comments="task.comments"
            :submit-function="updateTaskDossierComments"
            :comment-deletion-success-callback="refresh"
            :comment-reaction-success-callback="refresh"
          />
        </div>
        <!--#endregion -->
        <ActivityLogModal
          v-model:open="activityLogModalOpened"
          :model-id="task.id"
          model-type="task-dossier"
        />
      </div>
    </template>
  </BaseSlideOverModal>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import BaseSlideOverModal from '@c/addf-package/components/modal/BaseSlideOverModal.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import BaseShowEditTextArea from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTextArea.vue'
import BaseShowEditRadio from '@c/addf-package/components/BaseShowEditRadioGroup/BaseShowEditRadio.vue'
import BaseShowEditDateTimePickerv2 from '@c/addf-package/components/BaseShowEditDatePicker/BaseShowEditDateTimePickerv2.vue'
import SelectUser from '@c/InitialStateFormComponent/SelectUser.vue'
import UsersSelectorComponent from '@c/BaseSelectorComponent/UsersSelectorComponent.vue'
import SelectFuneralHome from '@c/InitialStateFormComponent/SelectFuneralHome.vue'
import SelectTasks from '@c/InitialStateFormComponent/Models/SelectTasks.vue'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import SelectMiniStatusTask from '@/views/dossier/form/subpages/taskDossier/components/taskDossierList/components/SelectMiniStatusTask.vue'
import CommentBox from '@c/CommentBox.vue'
import SelectDossier from '@c/SelectAjaxComponent/SelectDossier.vue'
import ActivityLogModal from '@ui/modal/activityLog/ActivityLogModal.vue'

export default {
  name: 'TaskDossierModal',
  components: {
    ActivityLogModal,
    SelectDossier,
    CommentBox,
    UsersSelectorComponent,
    SelectUser,
    BaseShowEditDateTimePickerv2,
    SelectMiniStatusTask,
    SelectTasks,
    BaseShowEditTextArea,
    BaseShowEditInput,
    BaseShowEditRadio,
    SelectFuneralHome,
    LoadingComponent,
    BaseButton,
    BaseSlideOverModal
  },
  props: {
    open: {
      type: Boolean,
      required: true
    },
    taskId: {
      type: [String, Number],
      required: false,
      default: null
    },
    defaultFuneralHomeIdForNewTaskDossier: {
      type: [String, Number],
      required: false,
      default: null
    },
    defaultParentType: {
      type: String,
      required: false,
      default: null
    },
    defaultParentId: {
      type: [String, Number],
      required: false,
      default: null
    },
    defaultDossierName: {
      type: String,
      required: false,
      default: null
    },
    defaultRemarks: {
      type: [String, null],
      required: false,
      default: null
    },
    lockedDossierId: {
      type: [String, Number],
      required: false,
      default: null
    },
    refreshTaskDossier: {
      type: Boolean,
      required: false,
      default: false
    },
    editionMode: {
      type: Boolean,
      required: false,
      default: false
    },
    draftMode: {
      type: Boolean,
      required: false,
      default: false
    },
    draftTask: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: ['update:open', 'update:editionMode', 'refresh-list', 'tasks-updated'],
  data() {
    return {
      task: null,
      type: 'existing',
      activityLogModalOpened: false,
      isSubmitting: false
    }
  },
  computed: {
    ...mapGetters({
      dossier: 'dossier/getDossier',
      initialMetadata: 'metadata/getInitialMetadata',
      errors: 'auth/getErrors',
      metadataTasks: 'metadata/getMetadataTasks'
    }),
    localEditionMode: {
      get() {
        return this.editionMode
      },
      set(value) {
        this.$emit('update:editionMode', value)
      }
    },
    localOpen: {
      get() {
        return this.open
      },
      set(value) {
        this.$emit('update:open', value)
      }
    },
    funeralHomeInputCondition() {
      return !this.defaultParentId && !this.defaultParentType && !this.taskId
    },
    dossierSelectDefaultAjax() {
      if (!this.task.parent || !this.taskId) {
        return { id: this.defaultParentId, title: this.defaultDossierName }
      } else {
        if (this.task.parent_type === 'App\\Models\\Dossier') {
          return { id: this.task.parent_id, title: this.task.parent.title }
        } else {
          return {
            id: this.task.parent.dossier.id,
            title: this.task.parent.dossier.title
          }
        }
      }
    },
    selectFuneralHomeCondition() {
      if (!this.defaultFuneralHomeIdForNewTaskDossier) {
        if (this.taskId) {
          return !(!!this.task.parent_type && !!this.task.parent_id)
        } else {
          return true
        }
      }
      return false
    },
    modalTitle() {
      if (this.taskId) return this.task?.name[this.$i18n.locale]
      return this.$t('global.add_task')
    },
    editionButtonCondition() {
      return !!this.taskId
    },
    submitButtonCondition() {
      if (!this.taskId) return true
      return this.localEditionMode
    },
    submitButtonTitle() {
      return this.taskId
        ? this.$t('button.update_task')
        : this.$t('button.create_task')
    }
  },
  watch: {
    'task.task_id'(newVal) {
      if (this.draftMode && newVal && this.metadataTasks) {
        const template = this.metadataTasks.find((t) => t.id === newVal)
        if (template?.name) {
          if (typeof template.name === 'string') {
            this.task.name[this.$i18n.locale] = template.name
          } else {
            this.task.name = { ...template.name }
          }
        }
      }
    },
    open(newValue) {
      if (newValue) {
        if (this.taskId) {
          this.processFetchTaskDossierByIdAction({ id: this.taskId }).then(
            (response) => {
              this.task = response.task_dossier
              this.task.due_date_time = this.normalizeDatetime(this.task.due_date_time)
            }
          )
        } else {
          if (this.draftMode && this.draftTask) {
            this.task = this.draftTask
            this.localEditionMode = true
          } else {
            this.processFetchNewTaskDossierAction({
              params: {
                defaultParentType: this.defaultParentType,
                defaultParentId: this.defaultParentId
              }
            }).then((response) => {
              this.task = response.TaskDossier
              this.task.due_date_time = this.normalizeDatetime(this.task.due_date_time)
              if (this.defaultRemarks && this.task.remarks && !this.draftMode) {
                this.task.remarks[this.$i18n.locale] = this.defaultRemarks
              }
              this.localEditionMode = true
            })
          }
        }
      } else {
        if (!this.draftMode) {
          this.$emit('refresh-list')
        }
      }
    }
  },
  methods: {
    ...mapMutations({
      processSetParentDossierTasksMutation: 'dossier/setParentDossierTasks'
    }),
    ...mapActions({
      processFetchNewTaskDossierAction: 'taskDossier/fetchNewTaskDossier',
      processFetchTaskDossierByIdAction: 'taskDossier/fetchTaskDossierById',
      processStoreTaskDossierAction: 'taskDossier/storeTaskDossier',
      processUpdateDossierTaskAction: 'dossier/updateDossierTask',
      processAddCommentToTaskDossierAction: 'comments/addCommentToTaskDossier',
      processDeleteTaskDossierByIdAction: 'dossier/deleteTaskDossierById',
      processUndoDeleteTaskDossierByIdAction: 'dossier/undoDeleteTaskDossierById'
    }),
    normalizeDatetime(value) {
      if (!value) return value
      const m = moment(value, ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD HH:mm:ss', 'YY-MM-DD HH:mm'])
      return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : value
    },
    refresh() {
      this.processFetchTaskDossierByIdAction({ id: this.taskId })
    },
    emitParentTasksUpdated(tasks) {
      const parentType = this.task.parent_type
      const parentId = this.task.parent_id
      if (tasks && parentType && parentId) {
        this.processSetParentDossierTasksMutation({
          parent_type: parentType,
          parent_id: parentId,
          task_dossiers: tasks
        })
        this.$emit('tasks-updated', {
          parent_type: parentType,
          parent_id: parentId,
          tasks
        })
      }
    },
    async deleteTask() {
      await this.processDeleteTaskDossierByIdAction({
        task_id: this.task.id
      }).then((response) => {
        this.emitParentTasksUpdated(response.tasks)
        this.localOpen = false
        this.$emit('refresh-list')
        this.$h.toastAction(
          'delete',
          this.$t('toasts.deleted_f', { var: this.$t('attributes.task') }),
          null,
          [
            {
              title: this.$t('button.cancel'),
              class: 'text-red-700',
              action: () => this.undoDeleteTask(response.id)
            }
          ]
        )
      })
    },
    async undoDeleteTask(id) {
      await this.processUndoDeleteTaskDossierByIdAction({
        task_id: id
      }).then((response) => {
        this.emitParentTasksUpdated(response.tasks)
        this.$emit('refresh-list')
      })
    },
    updateTaskDossierComments(text) {
      this.processAddCommentToTaskDossierAction({
        task_dossier_id: this.task.id,
        text: text
      }).then((response) => {
        this.$h.toastSuccess(this.$t('toasts.comment_added'))
        this.task.comments = response.comments
      })
    },
    async submitForm() {
      if (this.isSubmitting) return
      this.isSubmitting = true

      try {
        if (this.draftMode && !this.taskId) {
          this.localOpen = false
          return
        }

        if (this.taskId) {
          await this.processUpdateDossierTaskAction({
            id: this.task.id,
            task: this.task
          }).then((_response) => {
            this.isSubmitting = false
            this.$h.toastSuccess(this.$t('toasts.task_dossier_updated'))
          })
        } else {
          await this.processStoreTaskDossierAction({
            task_dossier: {
              ...this.task,
              parent_id: this.defaultParentId,
              parent_type: this.defaultParentType
            }
          }).then((response) => {
            this.isSubmitting = false
            this.$h.toastSuccess(this.$t('toasts.task_dossier_created'))
            if (
              response?.tasks &&
              this.defaultParentId &&
              this.defaultParentType
            ) {
              this.processSetParentDossierTasksMutation({
                parent_type: this.defaultParentType,
                parent_id: this.defaultParentId,
                task_dossiers: response.tasks
              })
              this.$emit('tasks-updated', {
                parent_type: this.defaultParentType,
                parent_id: this.defaultParentId,
                tasks: response.tasks
              })
            }
          })
        }

        this.$emit('refresh-list')
        this.localOpen = false
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>
